import { IXmlNode } from '@/app-modules/server-communications';
import { IPrizeStructure, IPrizeTicketBook, IPrizeStake, IPrizeElement } from '@/components/game/implementations/_default/_domain/models';
import { CatchUpData, ResetData, CallsData, NextGameData, PrizeData } from '@/components/game/_domain/server/prize/prize-data';
import { safeCast } from '@/helper';
import { getGameConfiguration } from '@/components/game/_domain/helpers';
import { ConfigurationPaths } from '@/app-modules/config/models';

class HamsterRacePrizeData extends PrizeData {

  constructor(node: IXmlNode) {
    super(node);
  }

  private get stakeValues(): number[] {
    return [25, 50, 100, 200];
  }

  private get hamsterSequence(): string[] {
    return [
        'one',
        'two',
        'three',
        'four',
        'five',
        'six',
        'seven',
        'eight'
    ];
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    return {
      stakes: this.stakeValues.map((stakeValue) => this.getPrizeStakeForStakeValue(stakeValue, +node.attributes.hamsterset, node))
    };
  }

  private getPrizeStakeForStakeValue(stakeValue: number, hamsterSet: number, node: IXmlNode): IPrizeStake {
    return {
      stakeValue,
       ticketBooks: this.hamsterSequence.map((hamsterId) => this.getPrizeStructureForStakeValueAndTicketBook(stakeValue, hamsterSet, hamsterId, node))
    };
  }

  private getPrizeStructureForStakeValueAndTicketBook(stakeValue: number, hamsterSet: number, hamsterId: string, node: IXmlNode): IPrizeTicketBook {
    const prizeValueCsv = this.getPrizeValueCsvForStakeValue(stakeValue, node);
    return {
      id: hamsterId.toString(),
      name: this.getHamsterImageUrlForHamsterSetId(hamsterSet, hamsterId),
      prizes: [ this.getPrize(prizeValueCsv, hamsterId) ]
    };
  }

  private getPrizeValueCsvForStakeValue(stakeValue: number, node: IXmlNode): string {
    const stakeIndex = this.stakeValues.indexOf(stakeValue);
    const stakeMultiplier = Math.pow(2, stakeIndex);
    const prizeBaseValues = this.getPrizeBaseValues();
    return prizeBaseValues.map((prizeValue) => +prizeValue * stakeMultiplier).join(',');
  }

  private getPrizeBaseValues(): string[] {
    const gameConfiguration = getGameConfiguration();
    const gameConfigurationPaths = ConfigurationPaths.forGame('hamsterrace');
    const prizes = gameConfiguration.getConfigurationByPath(gameConfigurationPaths.prizes);
    return prizes.split(',');
  }

  private getPrize(prizeCsv: string, hamsterId: string): IPrizeElement {

    return {
      name: 'Prize',
      prize: this.getPrizeValueForHamster(prizeCsv, hamsterId),
      disablePrize: false,
      isJackpot: false,
      jackpotCalls: 0
    };
  }

  private getPrizeValueForHamster(prizeCsv: string, hamsterId: string): number {
    if (prizeCsv) {
      const ticketBookIndex = this.getHamsterIndexForHamsterId(hamsterId);
      const prizeValues = prizeCsv.split(',');
      if (ticketBookIndex >= 0 && ticketBookIndex < prizeValues.length) {
        return Number(prizeValues[ticketBookIndex]);
      }
    }
    return 0;
  }

  private getHamsterIndexForHamsterId(hamsterId: string): number {
    return this.hamsterSequence.indexOf(hamsterId);
  }

  private getHamsterImageUrlForHamsterSetId(hamsterSet: number, hamsterId: string): string {
      const url = `/static/games/hamsterrace/skins/set-${hamsterSet}`;
      switch (hamsterId) {
        case 'one':
            return `${url}/1.png`;
        case 'two':
            return `${url}/2.png`;
        case 'three':
            return `${url}/3.png`;
        case 'four':
            return `${url}/4.png`;
        case 'five':
            return `${url}/5.png`;
        case 'six':
            return `${url}/6.png`;
        case 'seven':
            return `${url}/7.png`;
        case 'eight':
            return `${url}/8.png`;
      }
   }
}

class HamsterRaceCatchUpData extends CatchUpData {
  private prizeData: HamsterRacePrizeData;
  public hamsterSet: number;
  public gameType: number;
  public gameSequence: string;
  public stepsTaken: number;

  constructor(node: IXmlNode) {
    super(node);
    this.hamsterSet = safeCast(node.attributes.hamsterset, 1);
    this.gameType = safeCast(node.attributes.gametype, 1);
    this.gameSequence = node.attributes.calls;
    this.stepsTaken = safeCast(node.attributes.stepstaken, 0);
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    if (!this.prizeData) {
      this.prizeData = new HamsterRacePrizeData(node);
    }
    return this.prizeData.prizeStructure;
  }
}

class HamsterRaceResetData extends ResetData {
  private prizeData: HamsterRacePrizeData;
  public hamsterSet: number;
  public gameType: number;
  public gameSequence: string;

  constructor(node: IXmlNode) {
    super(node);
    this.hamsterSet = safeCast(node.attributes.hamsterset, 1);
    this.gameType = safeCast(node.attributes.gametype, 1);
    this.gameSequence = node.attributes.full;
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    if (!this.prizeData) {
      this.prizeData = new HamsterRacePrizeData(node);
    }
    return this.prizeData.prizeStructure;
  }
}

class HamsterRaceCallsData extends CallsData {
  constructor(node: IXmlNode) {
    super(node);
  }
}

class HamsterRaceNextGameData extends NextGameData {
  private prizeData: HamsterRacePrizeData;

  constructor(node: IXmlNode) {
    super(node);
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    if (!this.prizeData) {
      this.prizeData = new HamsterRacePrizeData(node);
    }
    return this.prizeData.prizeStructure;
  }
}

export {
  HamsterRaceCatchUpData,
  HamsterRaceResetData,
  HamsterRaceCallsData,
  HamsterRaceNextGameData
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/game/implementations/uk/hamster-race/_domain/models/prize.ts